import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import AboutOverlay from './AboutOverlay';

const fontStack = "'Century Gothic', 'CenturyGothic', 'Tw Cen MT', 'Futura', 'Trebuchet MS', 'Nunito', sans-serif";

const letters = [
  { char: 'S', label: 'Studio', full: 'Studio', desc: 'Video production house.', url: 'https://vicinity.studio', bg: '#4A5A67', accent: '#8AABB8' },
  { char: 'E', label: 'Events', full: 'Events', desc: 'Event production and AV support.', url: 'https://vicinity.events', bg: '#C37A53', accent: '#E8B99A' },
  { char: 'A', label: 'Agency', full: 'Agency', desc: 'Social media, strategy and campaigns.', url: 'https://vicinity.agency', bg: '#1A041D', accent: '#9B72A3' },
  { char: 'D', label: 'Design', full: 'Design', desc: 'Motion graphics and visual design.', url: 'https://vicinity.design', bg: '#194a68', accent: '#5A9DC0' },
];

const IDLE_BG = '#F3F3F3';
const IDLE_STOP = '#E8E4DF';

const logoDark = "https://media-manager-c.questera.ai/greta-media/f3431c01043f6ae8468982ada2c320c1b5f2e30df31f9c1fc9d61ccd4574fa71cea2ac7949ae73f0d2854f304172067e/images/aW1hZ2UvcG5n/605e41cdfe1fba190cb955ec2fc1ec93.png";
const logoLight = "https://media-manager-c.questera.ai/greta-media/f3431c01043f6ae8468982ada2c320c1b5f2e30df31f9c1fc9d61ccd4574fa71cea2ac7949ae73f0d2854f304172067e/images/aW1hZ2UvcG5n/5dd897f97c55e1aa90164e2d5d40e6e9.png";

export default function App() {
  const [hovered, setHovered] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  
  const containerRef = useRef(null);
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const springX = useSpring(rawX, { stiffness: 40, damping: 30 });
  const springY = useSpring(rawY, { stiffness: 40, damping: 30 });

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleMouseMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.width / 2, cy = rect.height / 2;
    rawX.set(((e.clientX - rect.left - cx) / cx) * 6);
    rawY.set(((e.clientY - rect.top - cy) / cy) * 4);
  };

  const active = hovered !== null ? letters[hovered] : null;

  return (
    <motion.div 
      ref={containerRef} 
      className="relative min-h-screen w-full flex flex-col items-center justify-between overflow-hidden" 
      style={{ fontFamily: fontStack }} 
      onMouseMove={handleMouseMove} 
    >
      <AnimatedBackground active={active} hovered={hovered} />
      <HoverBackgroundImage hovered={hovered} />
      <NoiseLayer />
      <ParticleField active={active} hovered={hovered} />
      <OrbLayer active={active} springX={springX} springY={springY} />

      <header className="z-10 w-full flex flex-col items-center pt-10 md:pt-12 gap-3">
        <motion.div 
          initial={{ opacity: 0, y: -20 }} 
          animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : -20 }} 
          transition={{ duration: 1.0, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} 
        >
          <LogoMark active={active} />
        </motion.div>
        
        <motion.p 
          initial={{ opacity: 0, y: 10 }} 
          animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 10 }} 
          transition={{ duration: 1.0, delay: 0.7, ease: [0.16, 1, 0.3, 1] }} 
          style={{ 
            fontFamily: fontStack, 
            fontSize: 'clamp(10px, 1.1vw, 13px)', 
            letterSpacing: '0.22em', 
            color: active ? 'rgba(255, 255, 255, 0.55)' : 'rgba(42, 42, 42, 0.48)', 
            transition: 'color 0.6s ease', 
            textTransform: 'uppercase' 
          }} 
        >
          Inspire Others
        </motion.p>
      </header>

      <main className="z-10 flex-1 flex flex-col items-center justify-center w-full px-4">
        <SeadRow hovered={hovered} setHovered={setHovered} active={active} loaded={loaded} springX={springX} springY={springY} />
        <SeadLabelsRow hovered={hovered} active={active} loaded={loaded} />
        <SeadDescRow hovered={hovered} active={active} loaded={loaded} />
        <SeadEtymology active={active} loaded={loaded} />
      </main>

      <Footer active={active} loaded={loaded} onAboutClick={() => setAboutOpen(true)} />

      <AnimatePresence>
        {aboutOpen && <AboutOverlay onClose={() => setAboutOpen(false)} />}
      </AnimatePresence>
    </motion.div>
  );
}

/* ─── Hover Background Image ──────────────────────────────────────────────── */
const hoverImages = [
  "https://media-manager-c.questera.ai/greta-media/f3431c01043f6ae8468982ada2c320c1b5f2e30df31f9c1fc9d61ccd4574fa71cea2ac7949ae73f0d2854f304172067e/images/aW1hZ2UvcG5n/9554e861d9c1cfa4784b78a8d30cc5d6.png", // Studio (S)
  "https://media-manager-c.questera.ai/greta-media/f3431c01043f6ae8468982ada2c320c1b5f2e30df31f9c1fc9d61ccd4574fa71cea2ac7949ae73f0d2854f304172067e/images/aW1hZ2UvcG5n/f8273454396fa4e0917a38c7f4616d88.png", // Events (E)
  "https://media-manager-c.questera.ai/greta-media/f3431c01043f6ae8468982ada2c320c1b5f2e30df31f9c1fc9d61ccd4574fa71cea2ac7949ae73f0d2854f304172067e/images/aW1hZ2UvanBlZw==/32da6dfb13148972f26a280fb997166d.jpg", // Agency (A)
  "https://media-manager-c.questera.ai/greta-media/f3431c01043f6ae8468982ada2c320c1b5f2e30df31f9c1fc9d61ccd4574fa71cea2ac7949ae73f0d2854f304172067e/images/aW1hZ2UvcG5n/51d9ff8a5fa969302491c85fa62f66f4.png"  // Design (D)
];

function HoverBackgroundImage({ hovered }) {
  return (
    <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
      {hoverImages.map((url, index) => {
        if (!url) return null;
        return (
          <motion.div
            key={index}
            className="absolute inset-0"
            style={{
              backgroundImage: `url("${url}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ 
              opacity: hovered === index ? 0.15 : 0, 
              scale: hovered === index ? 1 : 1.05 
            }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          />
        );
      })}
    </div>
  );
}

/* ─── SEAD Labels Row ─────────────────────────────────────────────────────── */
function SeadLabelsRow({ hovered, active, loaded }) {
  return (
    <motion.div 
      className="flex flex-row items-start justify-center w-full max-w-6xl mt-4 md:mt-5" 
      initial={{ opacity: 0, y: 10 }} 
      animate={loaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }} 
      transition={{ duration: 1.0, delay: 1.8, ease: [0.16, 1, 0.3, 1] }} 
    >
      {letters.map((letter, index) => {
        const isHov = hovered === index;
        const isOther = hovered !== null && hovered !== index;
        const baseColor = active ? 'rgba(255, 255, 255, 0.28)' : 'rgba(42, 42, 42, 0.28)';
        
        return (
          <motion.div 
            key={letter.char} 
            className="flex flex-col items-center" 
            style={{ flex: '1 1 0', minWidth: 0 }} 
            animate={{ opacity: isOther ? 0.15 : 1 }} 
            transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }} 
          >
            <motion.span 
              style={{ 
                fontFamily: fontStack, 
                fontSize: 'clamp(8px, 1vw, 13px)', 
                fontWeight: isHov ? 700 : 400, 
                letterSpacing: isHov ? '0.18em' : '0.25em', 
                textTransform: 'uppercase', 
                color: isHov ? letter.accent : baseColor, 
                transition: 'color 0.45s ease, font-weight 0.3s ease, letter-spacing 0.3s ease', 
                textAlign: 'center', 
                display: 'block' 
              }} 
            >
              {letter.full}
            </motion.span>
          </motion.div>
        );
      })}
    </motion.div>
  );
}

/* ─── SEAD Descriptions Row ───────────────────────────────────────────────── */
function SeadDescRow({ hovered, active, loaded }) {
  return (
    <div className="flex flex-row items-start justify-center w-full max-w-6xl mt-3 md:mt-4" style={{ minHeight: 36 }}>
      {letters.map((letter, index) => {
        const isHov = hovered === index;
        return (
          <div key={letter.char} className="flex flex-col items-center" style={{ flex: '1 1 0', minWidth: 0, padding: '0 8px' }}>
            <AnimatePresence mode="wait">
              {isHov && (
                <motion.div 
                  key="desc" 
                  initial={{ opacity: 0, y: 10, filter: 'blur(6px)', scale: 0.95 }} 
                  animate={{ opacity: 1, y: 0, filter: 'blur(0px)', scale: 1 }} 
                  exit={{ opacity: 0, y: -6, filter: 'blur(6px)', scale: 0.97 }} 
                  transition={{ duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }} 
                  style={{ position: 'relative', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }} 
                >
                  <motion.div 
                    initial={{ scaleX: 0 }} 
                    animate={{ scaleX: 1 }} 
                    exit={{ scaleX: 0 }} 
                    transition={{ duration: 0.35, ease: [0.25, 0.4, 0.25, 1] }} 
                    style={{ height: 1, width: '40%', backgroundColor: letter.accent, opacity: 0.5, transformOrigin: 'center' }} 
                  />
                  <motion.span 
                    style={{ 
                      fontFamily: fontStack, 
                      fontSize: 'clamp(9px, 0.95vw, 13px)', 
                      fontWeight: 600, 
                      letterSpacing: '0.12em', 
                      color: letter.accent, 
                      textAlign: 'center', 
                      display: 'block', 
                      lineHeight: 1.6, 
                      textShadow: `0 0 20px ${letter.accent}66` 
                    }} 
                  >
                    {letter.desc}
                  </motion.span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}

/* ─── SEAD Etymology ──────────────────────────────────────────────────────── */
function SeadEtymology({ active, loaded }) {
  return (
    <motion.div 
      className="flex flex-col items-center gap-[5px] mt-8 md:mt-10" 
      initial={{ opacity: 0, y: 8 }} 
      animate={loaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }} 
      transition={{ duration: 1.2, delay: 2.4, ease: [0.16, 1, 0.3, 1] }} 
    >
      <motion.p 
        style={{ 
          fontFamily: fontStack, 
          fontSize: 'clamp(8px, 0.8vw, 10px)', 
          letterSpacing: '0.18em', 
          fontStyle: 'italic', 
          color: active ? 'rgba(255, 255, 255, 0.32)' : 'rgba(42, 42, 42, 0.30)', 
          transition: 'color 0.6s ease', 
          textAlign: 'center', 
        }} 
      >
        Inspired by the Old English word 'sǣd' — seed
      </motion.p>
      <motion.p 
        style={{ 
          fontFamily: fontStack, 
          fontSize: 'clamp(7px, 0.72vw, 9px)', 
          letterSpacing: '0.22em', 
          textTransform: 'uppercase', 
          color: active ? 'rgba(255, 255, 255, 0.20)' : 'rgba(42, 42, 42, 0.20)', 
          transition: 'color 0.6s ease', 
          textAlign: 'center', 
        }} 
      >
        Where ideas begin
      </motion.p>
    </motion.div>
  );
}

/* ─── Canvas Animated Background ─────────────────────────────────────────── */
function AnimatedBackground({ active, hovered }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({ active: null, hovered: null, t: 0, raf: null });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const hexToRgb = (hex) => {
      const h = hex.replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };

    const lerp = (a, b, t) => a + (b - a) * t;
    const lerpColor = (c1, c2, t) => c1.map((v, i) => Math.round(lerp(v, c2[i], t)));

    const drawStudio = (w, h, t, accentC) => {
      const [ar, ag, ab] = accentC;
      const holeCount = 10, holeH = h / holeCount;
      [w * 0.04, w * 0.96].forEach((sx) => {
        for (let i = 0; i < holeCount; i++) {
          const hy = (i * holeH + t * 12) % h;
          const a = 0.08 + 0.04 * Math.sin(t * 3 + i);
          ctx.strokeStyle = `rgba(${ar}, ${ag}, ${ab}, ${a})`;
          ctx.lineWidth = 1;
          ctx.strokeRect(sx - 6, hy + 4, 12, holeH - 8);
        }
      });
      const vig = ctx.createRadialGradient(w / 2, h / 2, h * 0.2, w / 2, h / 2, h * 0.85);
      vig.addColorStop(0, 'rgba(0,0,0,0)');
      vig.addColorStop(1, 'rgba(0,0,0,0.35)');
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, w, h);
    };

    const drawEvents = (w, h, t, accentC) => {
      const [ar, ag, ab] = accentC;
      for (let s = 0; s < 3; s++) {
        const phase = t * 0.5 + s * (Math.PI * 2 / 3);
        const sx = w * (0.3 + 0.4 * Math.sin(phase));
        const tx = w * (0.2 + 0.6 * Math.cos(phase * 0.7 + s));
        const ty = h * 0.75, spread = w * 0.28;
        const spot = ctx.createRadialGradient(sx, 0, 0, tx, ty, spread);
        const a = 0.07 + 0.04 * Math.sin(t + s * 1.3);
        spot.addColorStop(0, `rgba(${ar}, ${ag}, ${ab}, ${a})`);
        spot.addColorStop(0.5, `rgba(${ar}, ${ag}, ${ab}, ${a * 0.4})`);
        spot.addColorStop(1, `rgba(${ar}, ${ag}, ${ab}, 0)`);
        ctx.fillStyle = spot;
        ctx.fillRect(0, 0, w, h);
      }
      const floor = ctx.createLinearGradient(0, h * 0.7, 0, h);
      floor.addColorStop(0, `rgba(${ar}, ${ag}, ${ab}, 0.06)`);
      floor.addColorStop(1, `rgba(${ar}, ${ag}, ${ab}, 0)`);
      ctx.fillStyle = floor;
      ctx.fillRect(0, h * 0.7, w, h * 0.3);
    };

    const drawAgency = (w, h, t, accentC) => {
      const [ar, ag, ab] = accentC;
      for (let wi = 0; wi < 3; wi++) {
        ctx.beginPath();
        const baseY = h * (0.35 + wi * 0.12), amp = h * (0.04 + wi * 0.015);
        const freq = 0.008 + wi * 0.003, speed = t * (1.2 + wi * 0.5);
        ctx.moveTo(0, baseY);
        for (let x = 0; x <= w; x += 4) {
          const y = baseY + amp * Math.sin(x * freq + speed) * Math.cos(x * freq * 0.4 + speed * 0.7);
          ctx.lineTo(x, y);
        }
        ctx.strokeStyle = `rgba(${ar}, ${ag}, ${ab}, ${0.06 - wi * 0.015})`;
        ctx.lineWidth = 1.5 - wi * 0.3;
        ctx.stroke();
      }
    };

    const drawDesign = (w, h, t, accentC) => {
      const [ar, ag, ab] = accentC;
      const cols = 8, rows = 5, cw = w / cols, ch = h / rows;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const phase = t * 0.6 + r * 0.4 + c * 0.3;
          const alpha = 0.025 * Math.abs(Math.sin(phase));
          ctx.strokeStyle = `rgba(${ar}, ${ag}, ${ab}, ${alpha})`;
          ctx.lineWidth = 0.5;
          ctx.strokeRect(c * cw + 4, r * ch + 4, cw - 8, ch - 8);
          if (Math.sin(phase * 0.5) > 0.6) {
            ctx.beginPath();
            ctx.moveTo(c * cw + 4, r * ch + 4);
            ctx.lineTo((c + 1) * cw - 4, (r + 1) * ch - 4);
            ctx.strokeStyle = `rgba(${ar}, ${ag}, ${ab}, ${alpha * 0.5})`;
            ctx.stroke();
          }
        }
      }
      const cx2 = w * 0.5, cy2 = h * 0.5, tr = Math.min(w, h) * 0.35;
      ctx.beginPath();
      for (let i = 0; i < 3; i++) {
        const angle = (i / 3) * Math.PI * 2 + t * 0.15;
        const px = cx2 + tr * Math.cos(angle);
        const py = cy2 + tr * Math.sin(angle);
        i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.strokeStyle = `rgba(${ar}, ${ag}, ${ab}, 0.04)`;
      ctx.lineWidth = 1;
      ctx.stroke();
    };

    const draw = () => {
      const { width: w, height: h } = canvas;
      const t = (stateRef.current.t += 0.003);
      const a = stateRef.current.active, hov = stateRef.current.hovered;
      const baseBg = a ? hexToRgb(a.bg) : hexToRgb(IDLE_BG);
      const stopBg = a ? hexToRgb(a.accent) : hexToRgb(IDLE_STOP);
      const accentC = a ? hexToRgb(a.accent) : [180, 170, 165];
      
      const gx = 0.5 + 0.38 * Math.sin(t * 0.6), gy = 0.5 + 0.32 * Math.cos(t * 0.44);
      const grad = ctx.createRadialGradient(w * gx, h * gy, 0, w * gx, h * gy, w * 0.9);
      const [r0, g0, b0] = baseBg, [r1, g1, b1] = lerpColor(baseBg, stopBg, 0.32);
      grad.addColorStop(0, `rgb(${r0}, ${g0}, ${b0})`);
      grad.addColorStop(0.55, `rgb(${r1}, ${g1}, ${b1})`);
      grad.addColorStop(1, `rgb(${r0}, ${g0}, ${b0})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);
      
      const sx = 0.5 + 0.45 * Math.cos(t * 0.38 + 1.2), sy = 0.5 + 0.4 * Math.sin(t * 0.28 + 0.8);
      const grad2 = ctx.createRadialGradient(w * sx, h * sy, 0, w * sx, h * sy, w * 0.6);
      const [ar, ag2, ab] = accentC;
      grad2.addColorStop(0, `rgba(${ar}, ${ag2}, ${ab}, 0.13)`);
      grad2.addColorStop(0.5, `rgba(${ar}, ${ag2}, ${ab}, 0.06)`);
      grad2.addColorStop(1, `rgba(${ar}, ${ag2}, ${ab}, 0)`);
      ctx.fillStyle = grad2;
      ctx.fillRect(0, 0, w, h);
      
      if (hov === 0) drawStudio(w, h, t * 5, accentC);
      else if (hov === 1) drawEvents(w, h, t * 5, accentC);
      else if (hov === 2) drawAgency(w, h, t * 5, accentC);
      else if (hov === 3) drawDesign(w, h, t * 5, accentC);
      else {
        const blobs = [
          { rx: 0.18 + 0.14 * Math.sin(t * 0.37 * 5), ry: 0.28 + 0.13 * Math.cos(t * 0.29 * 5), r: w * 0.34, a: 0.09 },
          { rx: 0.78 + 0.11 * Math.cos(t * 0.31 * 5), ry: 0.68 + 0.10 * Math.sin(t * 0.41 * 5), r: w * 0.30, a: 0.06 },
          { rx: 0.52 + 0.19 * Math.sin(t * 0.22 * 5), ry: 0.12 + 0.09 * Math.cos(t * 0.52 * 5), r: w * 0.24, a: 0.05 },
        ];
        blobs.forEach(({ rx, ry, r, a: alpha }) => {
          const bx = w * rx, by = h * ry;
          const bg2 = ctx.createRadialGradient(bx, by, 0, bx, by, r);
          bg2.addColorStop(0, `rgba(${ar}, ${ag2}, ${ab}, ${alpha})`);
          bg2.addColorStop(1, `rgba(${ar}, ${ag2}, ${ab}, 0)`);
          ctx.fillStyle = bg2;
          ctx.fillRect(0, 0, w, h);
        });
      }
      stateRef.current.raf = requestAnimationFrame(draw);
    };
    stateRef.current.raf = requestAnimationFrame(draw);
    
    return () => {
      cancelAnimationFrame(stateRef.current.raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => { stateRef.current.active = active; }, [active]);
  useEffect(() => { stateRef.current.hovered = hovered; }, [hovered]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-0 pointer-events-none" />;
}

/* ─── Themed Particle Field ───────────────────────────────────────────────── */
function ParticleField({ active, hovered }) {
  const canvasRef = useRef(null);
  const stateRef = useRef({ active: null, hovered: null, t: 0, raf: null, particles: [] });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles();
    };

    const initParticles = () => {
      const count = Math.floor((canvas.width * canvas.height) / 10000);
      stateRef.current.particles = Array.from({ length: count }, (_, i) => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        r: Math.random() * 2 + 0.3,
        vx: (Math.random() - 0.5) * 0.22,
        vy: (Math.random() - 0.5) * 0.14 - 0.05,
        life: Math.random(),
        speed: Math.random() * 0.004 + 0.001,
        shape: i % 4,
        angle: Math.random() * Math.PI * 2,
        spin: (Math.random() - 0.5) * 0.02,
      }));
    };
    resize();
    window.addEventListener('resize', resize);

    const hexToRgb = (hex) => {
      const h = hex.replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };

    const drawParticle = (p, pr, pg, pb, alpha, hov) => {
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.globalAlpha = alpha;
      
      if (hov === 0) {
        ctx.fillStyle = `rgb(${pr}, ${pg}, ${pb})`;
        if (p.shape < 2) {
          ctx.fillRect(-p.r, -p.r * 0.4, p.r * 2, p.r * 0.8);
        } else {
          ctx.beginPath();
          ctx.arc(0, 0, p.r * 0.5, 0, Math.PI * 2);
          ctx.fill();
        }
      } else if (hov === 1) {
        const spikes = 4, outerR = p.r * 1.4, innerR = p.r * 0.5;
        ctx.beginPath();
        for (let i = 0; i < spikes * 2; i++) {
          const r = i % 2 === 0 ? outerR : innerR;
          const a = (i / (spikes * 2)) * Math.PI * 2;
          i === 0 ? ctx.moveTo(r * Math.cos(a), r * Math.sin(a)) : ctx.lineTo(r * Math.cos(a), r * Math.sin(a));
        }
        ctx.closePath();
        ctx.fillStyle = `rgb(${pr}, ${pg}, ${pb})`;
        ctx.fill();
      } else if (hov === 2) {
        ctx.beginPath();
        ctx.arc(0, 0, p.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgb(${pr}, ${pg}, ${pb})`;
        ctx.lineWidth = 0.6;
        ctx.stroke();
        if (p.shape === 0) {
          ctx.beginPath();
          ctx.arc(0, 0, p.r * 0.35, 0, Math.PI * 2);
          ctx.fillStyle = `rgb(${pr}, ${pg}, ${pb})`;
          ctx.fill();
        }
      } else if (hov === 3) {
        if (p.shape < 2) {
          ctx.beginPath();
          ctx.moveTo(0, -p.r * 1.2);
          ctx.lineTo(p.r * 1.0, p.r * 0.8);
          ctx.lineTo(-p.r * 1.0, p.r * 0.8);
          ctx.closePath();
          ctx.strokeStyle = `rgb(${pr}, ${pg}, ${pb})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        } else {
          ctx.strokeStyle = `rgb(${pr}, ${pg}, ${pb})`;
          ctx.lineWidth = 0.6;
          ctx.strokeRect(-p.r, -p.r, p.r * 2, p.r * 2);
        }
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgb(${pr}, ${pg}, ${pb})`;
        ctx.fill();
      }
      ctx.restore();
    };

    const draw = () => {
      const { width: w, height: h } = canvas;
      const a = stateRef.current.active, hov = stateRef.current.hovered;
      ctx.clearRect(0, 0, w, h);
      const [pr, pg, pb] = a ? hexToRgb(a.accent) : [140, 130, 120];
      
      stateRef.current.particles.forEach((p) => {
        p.life += p.speed;
        p.angle += p.spin;
        if (p.life > 1) {
          p.life = 0;
          p.x = Math.random() * w;
          p.y = h + 10;
        }
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = w;
        if (p.x > w) p.x = 0;
        if (p.y < -20) p.y = h;
        const alpha = Math.sin(p.life * Math.PI) * (a ? 0.55 : 0.18);
        drawParticle(p, pr, pg, pb, alpha, hov);
      });
      stateRef.current.raf = requestAnimationFrame(draw);
    };
    stateRef.current.raf = requestAnimationFrame(draw);
    
    return () => {
      cancelAnimationFrame(stateRef.current.raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  useEffect(() => { stateRef.current.active = active; }, [active]);
  useEffect(() => { stateRef.current.hovered = hovered; }, [hovered]);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full z-0 pointer-events-none" style={{ mixBlendMode: 'screen' }} />;
}

/* ─── Noise Layer ─────────────────────────────────────────────────────────── */
function NoiseLayer() {
  return (
    <div 
      className="absolute inset-0 pointer-events-none opacity-[0.028] z-0" 
      style={{ 
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, 
        backgroundRepeat: 'repeat', 
        backgroundSize: '128px' 
      }} 
    />
  );
}

/* ─── Orb Layer ───────────────────────────────────────────────────────────── */
function OrbLayer({ active, springX, springY }) {
  const orb1X = useTransform(springX, v => v * -3);
  const orb1Y = useTransform(springY, v => v * -2);
  const orb2X = useTransform(springX, v => v * 2.5);
  const orb2Y = useTransform(springY, v => v * 2);
  const orb3X = useTransform(springX, v => v * -1.5);
  const orb3Y = useTransform(springY, v => v * 1.5);

  return (
    <>
      <motion.div 
        className="absolute top-[-5%] left-[0%] w-[60vw] h-[60vw] max-w-[640px] max-h-[640px] rounded-full pointer-events-none z-0" 
        style={{ filter: 'blur(150px)', backgroundColor: active ? active.accent : '#8AABB8', x: orb1X, y: orb1Y }} 
        animate={{ opacity: active ? 0.38 : 0.09, scale: [1, 1.08, 1, 0.94, 1] }} 
        transition={{ opacity: { duration: 1 }, scale: { duration: 22, repeat: Infinity, ease: 'easeInOut' } }} 
      />
      <motion.div 
        className="absolute bottom-[-5%] right-[-2%] w-[45vw] h-[45vw] max-w-[500px] max-h-[500px] rounded-full pointer-events-none z-0" 
        style={{ filter: 'blur(130px)', backgroundColor: active ? active.bg : '#C37A53', x: orb2X, y: orb2Y }} 
        animate={{ opacity: active ? 0.30 : 0.07, scale: [1, 0.93, 1, 1.08, 1] }} 
        transition={{ opacity: { duration: 1 }, scale: { duration: 18, repeat: Infinity, ease: 'easeInOut' } }} 
      />
      <motion.div 
        className="absolute top-[40%] left-[45%] w-[30vw] h-[30vw] max-w-[360px] max-h-[360px] rounded-full pointer-events-none z-0" 
        style={{ filter: 'blur(110px)', backgroundColor: active ? active.accent : '#9B72A3', x: orb3X, y: orb3Y }} 
        animate={{ opacity: active ? 0.18 : 0.04, scale: [1, 1.12, 0.96, 1.05, 1] }} 
        transition={{ opacity: { duration: 1.2 }, scale: { duration: 14, repeat: Infinity, ease: 'easeInOut' } }} 
      />
    </>
  );
}

/* ─── Logo ────────────────────────────────────────────────────────────────── */
function LogoMark({ active }) {
  return (
    <motion.div className="relative h-10 md:h-[52px]" style={{ width: 'auto' }} whileHover={{ scale: 1.05 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}>
      <img src={logoDark} alt="VICINITY" className="h-full w-auto object-contain" style={{ opacity: active ? 0 : 1, transition: 'opacity 0.6s' }} />
      <img src={logoLight} alt="VICINITY" className="h-full w-auto object-contain absolute inset-0" style={{ opacity: active ? 1 : 0, transition: 'opacity 0.6s' }} />
    </motion.div>
  );
}

/* ─── SEAD Row ────────────────────────────────────────────────────────────── */
function SeadRow({ hovered, setHovered, active, loaded, springX, springY }) {
  return (
    <div className="flex flex-row items-center justify-center gap-0 md:gap-2 w-full max-w-6xl">
      {letters.map((letter, index) => (
        <LetterBlock 
          key={letter.char} 
          letter={letter} 
          index={index} 
          hovered={hovered} 
          setHovered={setHovered} 
          active={active} 
          isHovered={hovered === index} 
          isOther={hovered !== null && hovered !== index} 
          loaded={loaded} 
          springX={springX} 
          springY={springY} 
        />
      ))}
    </div>
  );
}

/* ─── Themed Hover Overlays ───────────────────────────────────────────────── */
function StudioOverlay({ letter }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute left-0 top-0 bottom-0 w-[14%] flex flex-col justify-around items-center py-3">
        {[...Array(7)].map((_, i) => (
          <motion.div 
            key={i} 
            style={{ width: 10, height: 14, border: `1.5px solid ${letter.accent}`, borderRadius: 1 }} 
            initial={{ opacity: 0 }} 
            animate={{ opacity: [0, 0.55, 0.55, 0] }} 
            transition={{ duration: 2.4, delay: i * 0.08, repeat: Infinity, repeatDelay: 0.6 }} 
          />
        ))}
      </div>
      <div className="absolute right-0 top-0 bottom-0 w-[14%] flex flex-col justify-around items-center py-3">
        {[...Array(7)].map((_, i) => (
          <motion.div 
            key={i} 
            style={{ width: 10, height: 14, border: `1.5px solid ${letter.accent}`, borderRadius: 1 }} 
            initial={{ opacity: 0 }} 
            animate={{ opacity: [0, 0.55, 0.55, 0] }} 
            transition={{ duration: 2.4, delay: i * 0.08 + 0.12, repeat: Infinity, repeatDelay: 0.6 }} 
          />
        ))}
      </div>
      {[30, 70].map((pct, i) => (
        <motion.div 
          key={i} 
          className="absolute left-[14%] right-[14%]" 
          style={{ top: `${pct}%`, height: '1px', backgroundColor: letter.accent }} 
          initial={{ scaleX: 0, opacity: 0 }} 
          animate={{ scaleX: 1, opacity: 0.25 }} 
          transition={{ duration: 0.7, delay: 0.2 + i * 0.15 }} 
        />
      ))}
      <motion.div 
        className="absolute top-[8%] right-[18%] flex items-center gap-1" 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <motion.div 
          style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#FF4444' }} 
          animate={{ opacity: [1, 0.15, 1] }} 
          transition={{ duration: 1.0, repeat: Infinity, ease: 'easeInOut' }} 
        />
        <span style={{ fontFamily: fontStack, fontSize: 8, color: letter.accent, opacity: 0.7, letterSpacing: '0.15em' }}>REC</span>
      </motion.div>
      <FrameCounter letter={letter} />
    </div>
  );
}

function FrameCounter({ letter }) {
  const [frame, setFrame] = React.useState(0);
  useEffect(() => {
    const id = setInterval(() => setFrame(f => (f + 1) % 100), 80);
    return () => clearInterval(id);
  }, []);
  return (
    <motion.div 
      className="absolute bottom-[8%] left-[18%]" 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 0.55 }} 
      transition={{ delay: 0.4 }} 
      style={{ fontFamily: "'Courier New', monospace", fontSize: 9, color: letter.accent, letterSpacing: '0.1em' }}
    >
      {String(frame).padStart(2, '0')}:24
    </motion.div>
  );
}

function EventsOverlay({ letter }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[0, 1].map(i => (
        <motion.div 
          key={i} 
          className="absolute" 
          style={{ 
            top: 0, 
            left: i === 0 ? '20%' : '55%', 
            width: '60%', 
            height: '100%', 
            background: `conic-gradient(from ${i === 0 ? '-20deg' : '20deg'} at ${i === 0 ? '30%' : '70%'} 0%, transparent 0deg, ${letter.accent}22 18deg, transparent 36deg)`, 
            transformOrigin: i === 0 ? '30% 0%' : '70% 0%' 
          }} 
          animate={{ rotate: [i === 0 ? -12 : 12, i === 0 ? 12 : -12, i === 0 ? -12 : 12] }} 
          transition={{ duration: 3.5 + i, repeat: Infinity, ease: 'easeInOut' }} 
        />
      ))}
      <motion.div 
        className="absolute bottom-[18%] left-0 right-0" 
        style={{ height: '1px', backgroundColor: letter.accent }} 
        initial={{ scaleX: 0, opacity: 0 }} 
        animate={{ scaleX: 1, opacity: 0.3 }} 
        transition={{ duration: 0.6, ease: 'easeOut' }} 
      />
    </div>
  );
}

function AgencyOverlay({ letter }) {
  const chips = ['↑ 24%', '❤ 8.2k', '◎ 14k', '▲ 3.1x'];
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
        {[0, 1, 2].map(i => (
          <motion.path 
            key={i} 
            fill="none" 
            stroke={letter.accent} 
            strokeWidth={0.8 - i * 0.2} 
            strokeOpacity={0.25 - i * 0.06} 
            d={`M0,${40 + i * 18} C80,${20 + i * 10} 160,${60 + i * 12} 240,${40 + i * 18} S400,${20 + i * 10} 480,${40 + i * 18}`} 
            animate={{ 
              d: [
                `M0,${40 + i * 18} C80,${20 + i * 10} 160,${60 + i * 12} 240,${40 + i * 18} S400,${20 + i * 10} 480,${40 + i * 18}`,
                `M0,${50 + i * 18} C80,${70 + i * 10} 160,${30 + i * 12} 240,${50 + i * 18} S400,${70 + i * 10} 480,${50 + i * 18}`,
                `M0,${40 + i * 18} C80,${20 + i * 10} 160,${60 + i * 12} 240,${40 + i * 18} S400,${20 + i * 10} 480,${40 + i * 18}`
              ] 
            }} 
            transition={{ duration: 2.5 + i * 0.6, repeat: Infinity, ease: 'easeInOut' }} 
          />
        ))}
      </svg>
      {chips.map((chip, i) => (
        <motion.div 
          key={i} 
          className="absolute text-[8px] px-1.5 py-0.5 rounded-full" 
          style={{ 
            fontFamily: fontStack, 
            color: letter.accent, 
            border: `1px solid ${letter.accent}55`, 
            left: `${10 + i * 22}%`, 
            top: `${20 + (i % 2) * 45}%`, 
            backdropFilter: 'blur(4px)' 
          }} 
          initial={{ opacity: 0, y: 8 }} 
          animate={{ opacity: [0, 0.85, 0.85, 0], y: [8, 0, 0, -8] }} 
          transition={{ duration: 2.8, delay: i * 0.5, repeat: Infinity, repeatDelay: 0.8 }}
        >
          {chip}
        </motion.div>
      ))}
    </div>
  );
}

function DesignOverlay({ letter }) {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        <motion.polygon 
          points="50,10 88,80 12,80" 
          fill="none" 
          stroke={letter.accent} 
          strokeWidth="0.5" 
          strokeOpacity="0.35" 
          initial={{ pathLength: 0, opacity: 0 }} 
          animate={{ pathLength: 1, opacity: 1 }} 
          transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }} 
        />
        <motion.circle 
          cx="50" cy="55" r="16" 
          fill="none" 
          stroke={letter.accent} 
          strokeWidth="0.4" 
          strokeOpacity="0.25" 
          initial={{ pathLength: 0, opacity: 0 }} 
          animate={{ pathLength: 1, opacity: 1 }} 
          transition={{ duration: 1.2, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} 
        />
        <motion.line 
          x1="50" y1="5" x2="50" y2="95" 
          stroke={letter.accent} 
          strokeWidth="0.3" 
          strokeOpacity="0.15" 
          initial={{ scaleY: 0 }} 
          animate={{ scaleY: 1 }} 
          transition={{ duration: 0.8, delay: 0.5 }} 
          style={{ transformOrigin: '50px 50px' }} 
        />
        <motion.line 
          x1="5" y1="50" x2="95" y2="50" 
          stroke={letter.accent} 
          strokeWidth="0.3" 
          strokeOpacity="0.15" 
          initial={{ scaleX: 0 }} 
          animate={{ scaleX: 1 }} 
          transition={{ duration: 0.8, delay: 0.5 }} 
          style={{ transformOrigin: '50px 50px' }} 
        />
        {[
          ['M8,18 L8,8 L18,8'],
          ['M82,8 L92,8 L92,18'],
          ['M8,82 L8,92 L18,92'],
          ['M82,92 L92,92 L92,82']
        ].map(([d], i) => (
          <motion.path 
            key={i} 
            d={d} 
            fill="none" 
            stroke={letter.accent} 
            strokeWidth="0.6" 
            strokeOpacity="0.4" 
            initial={{ pathLength: 0, opacity: 0 }} 
            animate={{ pathLength: 1, opacity: 1 }} 
            transition={{ duration: 0.5, delay: 0.8 + i * 0.1 }} 
          />
        ))}
      </svg>
    </div>
  );
}

/* ─── Letter Block ────────────────────────────────────────────────────────── */
const FLOAT_CONFIGS = [
  { phase: 0.0, amp: 6, speed: 0.016 },
  { phase: 1.4, amp: 4, speed: 0.020 },
  { phase: 2.8, amp: 7, speed: 0.014 },
  { phase: 4.2, amp: 5, speed: 0.018 },
];

function LetterBlock({ letter, index, setHovered, active, isHovered, isOther, loaded, springX, springY }) {
  const baseColor = active ? '#FFFFFF' : '#2A2A2A';
  const parallaxX = useTransform(springX, v => v * (index % 2 === 0 ? -0.7 : 0.7));
  const floatY = useMotionValue(0);

  useEffect(() => {
    let raf;
    const cfg = FLOAT_CONFIGS[index];
    let t = cfg.phase;
    const loop = () => {
      t += cfg.speed;
      floatY.set(Math.sin(t) * cfg.amp);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [index]);

  const overlayMap = [StudioOverlay, EventsOverlay, AgencyOverlay, DesignOverlay];
  const Overlay = overlayMap[index];
  const letterDelay = 1.0 + index * 0.15;

  return (
    <motion.a 
      href={letter.url} 
      className="relative flex flex-col items-center justify-center cursor-pointer select-none" 
      style={{ flex: '1 1 0', minWidth: 0, textDecoration: 'none' }} 
      onMouseEnter={() => setHovered(index)} 
      onMouseLeave={() => setHovered(null)} 
      animate={{ opacity: isOther ? 0.10 : 1 }} 
      transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }} 
    >
      <motion.div className="relative flex items-center justify-center w-full" style={{ x: parallaxX, y: isHovered ? 0 : floatY }}>
        <motion.div 
          className="relative flex items-center justify-center w-full" 
          initial={{ opacity: 0, y: 40 }} 
          animate={loaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }} 
          transition={{ duration: 1.1, delay: letterDelay, ease: [0.16, 1, 0.3, 1] }}
        >
          <motion.div 
            className="absolute inset-0 rounded-full pointer-events-none" 
            style={{ filter: 'blur(100px)', backgroundColor: letter.accent }} 
            animate={{ opacity: isHovered ? 0.65 : 0, scale: isHovered ? 1.1 : 0.8 }} 
            transition={{ duration: 0.6, ease: 'easeOut' }} 
          />
          <AnimatePresence>
            {isHovered && (
              <motion.div 
                key="overlay" 
                className="absolute inset-0 pointer-events-none" 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                transition={{ duration: 0.4 }}
              >
                <Overlay letter={letter} />
              </motion.div>
            )}
          </AnimatePresence>
          <motion.span 
            className="relative block text-center font-light leading-none" 
            style={{ 
              fontFamily: fontStack, 
              fontSize: 'clamp(5rem, 20vw, 18rem)', 
              letterSpacing: '-0.02em', 
              color: baseColor, 
              WebkitTextStroke: isHovered ? `1px ${letter.accent}` : '0px transparent' 
            }} 
            animate={{ 
              color: isHovered ? letter.accent : baseColor, 
              y: isHovered ? -14 : 0, 
              scale: isHovered ? 1.06 : 1, 
              rotateX: isHovered ? 8 : 0, 
              textShadow: isHovered ? `0 0 60px ${letter.accent}80, 0 0 120px ${letter.accent}40` : '0 0 0px transparent' 
            }} 
            transition={{ duration: 0.55, ease: [0.25, 0.4, 0.25, 1] }} 
          >
            {letter.char}
          </motion.span>
        </motion.div>
      </motion.div>
    </motion.a>
  );
}

/* ─── Footer ──────────────────────────────────────────────────────────────── */
function Footer({ active, loaded, onAboutClick }) {
  const textColor = active ? 'rgba(255, 255, 255, 0.38)' : 'rgba(42, 42, 42, 0.36)';
  const strongColor = active ? 'rgba(255, 255, 255, 0.55)' : 'rgba(42, 42, 42, 0.52)';
  const dividerColor = active ? 'rgba(255, 255, 255, 0.12)' : 'rgba(42, 42, 42, 0.12)';
  const borderColor = active ? 'rgba(255, 255, 255, 0.18)' : 'rgba(42, 42, 42, 0.18)';
  const hoverBg = active ? 'rgba(255, 255, 255, 0.08)' : 'rgba(42, 42, 42, 0.06)';

  return (
    <motion.footer 
      className="z-10 w-full flex flex-col items-center pb-8 md:pb-10 pt-10 md:pt-12 gap-2" 
      style={{ fontFamily: fontStack }} 
      initial={{ opacity: 0, y: 12 }} 
      animate={loaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 12 }} 
      transition={{ duration: 1.0, delay: 2.0, ease: [0.16, 1, 0.3, 1] }} 
    >
      <div className="flex flex-row items-center gap-3 mb-4">
        <motion.button 
          onClick={onAboutClick} 
          initial={{ opacity: 0, y: 8 }} 
          animate={loaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }} 
          transition={{ duration: 1.0, delay: 2.1, ease: [0.16, 1, 0.3, 1] }} 
          whileHover={{ scale: 1.04 }} 
          whileTap={{ scale: 0.97 }} 
          style={{ 
            fontFamily: fontStack, 
            fontSize: 'clamp(8px, 0.85vw, 10px)', 
            letterSpacing: '0.28em', 
            textTransform: 'uppercase', 
            color: strongColor, 
            border: `1px solid ${borderColor}`, 
            borderRadius: 2, 
            padding: '7px 20px', 
            background: 'transparent', 
            cursor: 'pointer', 
            backdropFilter: 'blur(6px)', 
            transition: 'color 0.6s ease, border-color 0.6s ease', 
          }} 
        >
          About Us
        </motion.button>
        <motion.a 
          href="https://careers.vicinity.sg/" 
          target="_blank" 
          rel="noopener noreferrer" 
          initial={{ opacity: 0, y: 8 }} 
          animate={loaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }} 
          transition={{ duration: 1.0, delay: 2.2, ease: [0.16, 1, 0.3, 1] }} 
          whileHover={{ scale: 1.04 }} 
          whileTap={{ scale: 0.97 }} 
          style={{ textDecoration: 'none' }} 
        >
          <motion.div 
            style={{ 
              fontFamily: fontStack, 
              fontSize: 'clamp(8px, 0.85vw, 10px)', 
              letterSpacing: '0.28em', 
              textTransform: 'uppercase', 
              color: strongColor, 
              border: `1px solid ${borderColor}`, 
              borderRadius: 2, 
              padding: '7px 20px', 
              cursor: 'pointer', 
              backdropFilter: 'blur(6px)', 
              transition: 'color 0.6s ease, border-color 0.6s ease, background 0.3s ease', 
            }} 
            whileHover={{ backgroundColor: hoverBg }} 
          >
            Careers
          </motion.div>
        </motion.a>
      </div>
      <motion.div 
        style={{ height: '1px', backgroundColor: dividerColor, width: 0, marginBottom: 12 }} 
        animate={loaded ? { width: '48px' } : { width: 0 }} 
        transition={{ duration: 1.2, delay: 2.4, ease: [0.16, 1, 0.3, 1] }} 
      />
      <motion.p className="text-[10px] md:text-[11px] tracking-[0.3em] uppercase" animate={{ color: strongColor }} transition={{ duration: 0.6 }}>Vicinity</motion.p>
      <motion.p className="text-[9px] md:text-[10px] tracking-[0.18em] italic" animate={{ color: textColor }} transition={{ duration: 0.6 }}>Inspire Others</motion.p>
      <motion.p className="text-[8px] md:text-[9px] tracking-[0.14em] text-center mt-1" animate={{ color: textColor }} transition={{ duration: 0.6 }} style={{ opacity: 0.8 }}>A collective of creative disciplines.</motion.p>
      <motion.p className="text-[8px] tracking-[0.16em] mt-3" animate={{ color: textColor }} transition={{ duration: 0.6 }} style={{ opacity: 0.6 }}>© 2026 Vicinity</motion.p>
    </motion.footer>
  );
}